import { useState } from 'react'

import {
  Box,
  Card,
  CheckCircle,
  Text,
  TogglerCheckbox,
  useUnsafeCallback,
} from '@factorialco/deprecated-design-system'
import { F0Button } from '@factorialco/f0-react'
import { OneAlert } from '@factorialco/f0-react/dist/experimental'
import { Check, Cross } from '@factorialco/f0-react/icons/app'
import {
  CostCentersConfigurationsDocument,
  CostCentersStructureQuery,
  CreateCostCentersConfigurationDocument,
  JobCatalogEnumsJobCatalogTreeNodeTypesEnum,
} from 'generated/resources/graphql'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useUnsafeMemo } from 'modules/core/hooks/useMemo'
import { useMutation } from 'modules/core/hooks/useMutation'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'

import {
  Hierarchy,
  JobCatalogStructureMapping,
} from 'modules/cost_centers/components/CostCentersStructure/types'

import { CostCentersPreview } from './Preview'

const CostCenterConfiguration = ({
  cancel,
  data,
}: {
  cancel: () => void
  data?: CostCentersStructureQuery
}) => {
  const {
    company: { id: companyId },
  } = useCurrentSession()

  const [expandJobStructure, setExpandobStructure] = useState<boolean>(false)
  const [jobStructure, setJobStructure] = useState<JobCatalogEnumsJobCatalogTreeNodeTypesEnum>()

  const [createCostCentersConfiguration, { loading: creatingCostCentersConfiguration }] =
    useMutation(CreateCostCentersConfigurationDocument, {
      extractorFn: (data) => data.costCentersMutations.createCostcentersConfiguration,
      refetchQueries: [CostCentersConfigurationsDocument],
      awaitRefetchQueries: true,
    })

  const labels: Record<JobCatalogEnumsJobCatalogTreeNodeTypesEnum, string> = {
    jobcatalog_treefamily: i18n.t(
      'finance.cost_centers_structure.configuration.dimensions.options.job_structure.family'
    ),
    jobcatalog_treefunction: `${i18n.t(
      'finance.cost_centers_structure.configuration.dimensions.options.job_structure.family'
    )} > ${i18n.t(
      'finance.cost_centers_structure.configuration.dimensions.options.job_structure.function'
    )}`,
    jobcatalog_treerole: `${i18n.t(
      'finance.cost_centers_structure.configuration.dimensions.options.job_structure.family'
    )} > ${i18n.t(
      'finance.cost_centers_structure.configuration.dimensions.options.job_structure.function'
    )} > ${i18n.t(
      'finance.cost_centers_structure.configuration.dimensions.options.job_structure.role'
    )}`,
    jobcatalog_treelevel: '',
    jobcatalog_treeroot: '',
  }

  const treeArray = useUnsafeMemo(() => {
    try {
      const tree = data?.jobCatalog?.treesConnection?.nodes?.[0]
      if (!tree?.serializedTree) return []

      const parsed = JSON.parse(tree.serializedTree)
      return Array.isArray(parsed) ? parsed : [parsed]
    } catch (e) {
      return []
    }
  }, [data?.jobCatalog?.treesConnection?.nodes])

  const save = useUnsafeCallback(async () => {
    if (!jobStructure) return

    const structure = JobCatalogStructureMapping[jobStructure]

    if (structure) {
      const result = await createCostCentersConfiguration({ variables: { companyId, structure } })

      const handled = handleResult(result, () => {})

      if (handled.success) {
        cancel()
      }
    }
  }, [cancel, companyId, jobStructure])

  return (
    <Box
      flexGrow
      flexDirection="column"
      borderRadius={{ all: 'abs008' }}
      border={{ all: { style: 'solid', width: 's1', color: 'grey400' } }}
    >
      <Box
        paddingY="s8"
        paddingX="s16"
        flexDirection="row"
        alignItems="center"
        justifyContent="spaceBetween"
        border={{ bottom: { style: 'solid', width: 's1', color: 'grey400' } }}
      >
        <Text.Normal size="200" weight="semibold" color="grey1000">
          {i18n.t('finance.cost_centers_structure.configuration.title')}
        </Text.Normal>
        <F0Button variant="outline" size="md" onClick={cancel} icon={Cross} hideLabel />
      </Box>
      <Box flexDirection="row" flexGrow>
        <Box flexDirection="column" gap="s16" paddingX="s24" paddingY="s16" style={{ flex: '2' }}>
          <Box flexDirection="column" gap="s4">
            <Text.Normal size="300" weight="semibold" color="grey1000">
              {i18n.t('finance.cost_centers_structure.configuration.dimensions.title')}
            </Text.Normal>
            <Text.Normal size="200" color="grey1000">
              {i18n.t('finance.cost_centers_structure.configuration.dimensions.description')}
            </Text.Normal>
          </Box>
          <Box flexDirection="column">
            <Card>
              <Card.Title
                action={
                  <TogglerCheckbox
                    onChange={(value) => {
                      setExpandobStructure(value)
                      if (!value) setJobStructure(undefined)
                    }}
                    value={expandJobStructure}
                  />
                }
              >
                {i18n.t(
                  'finance.cost_centers_structure.configuration.dimensions.options.job_structure.title'
                )}
              </Card.Title>
              <Card.Description>
                {i18n.t(
                  'finance.cost_centers_structure.configuration.dimensions.options.job_structure.description'
                )}
              </Card.Description>
              {expandJobStructure && (
                <Card.Content>
                  <Box flexDirection="column" gap="s8" width="full" paddingTop="s8">
                    {Hierarchy.map((level) => {
                      return (
                        <Card onClick={() => setJobStructure(level)} key={level}>
                          <Card.Content>
                            <Box gap="s12" flexDirection="row" alignItems="center">
                              <CheckCircle checked={jobStructure === level} />
                              <Text.Normal size="200" weight="semibold" color="grey1000">
                                {labels[level]}
                              </Text.Normal>
                            </Box>
                          </Card.Content>
                        </Card>
                      )
                    })}
                  </Box>
                </Card.Content>
              )}
            </Card>
          </Box>
          <Box flexDirection="column">
            <Card>
              <Card.Title action={<TogglerCheckbox value={false} disabled />}>
                {i18n.t(
                  'finance.cost_centers_structure.configuration.dimensions.options.workplaces.title'
                )}
              </Card.Title>
              <Card.Description>
                {i18n.t(
                  'finance.cost_centers_structure.configuration.dimensions.options.workplaces.description'
                )}
              </Card.Description>
            </Card>
          </Box>
        </Box>
        <Box
          gap="s8"
          paddingY="s16"
          paddingX="s24"
          background="grey200"
          style={{ flex: '4' }}
          flexDirection="column"
        >
          <Box flexDirection="column" gap="s4">
            <Text.Normal size="200" weight="semibold" color="grey700">
              {i18n.t('finance.cost_centers_structure.preview.title')}
            </Text.Normal>
          </Box>
          {jobStructure ? (
            <Box flexDirection="column">
              <OneAlert
                variant="warning"
                title={i18n.t('finance.cost_centers_structure.preview.alert.title')}
                description={i18n.t('finance.cost_centers_structure.preview.alert.description')}
              />
            </Box>
          ) : null}
          <Box
            flexGrow
            background="white"
            flexDirection="column"
            borderRadius={{ all: 'abs008' }}
            border={{ all: { style: 'solid', width: 's1', color: 'grey400' } }}
          >
            <CostCentersPreview
              treeArray={treeArray}
              jobStructure={jobStructure}
              expandJobStructure={expandJobStructure}
            />
          </Box>
        </Box>
      </Box>
      <Box
        paddingY="s12"
        paddingX="s16"
        flexDirection="row"
        alignItems="center"
        justifyContent="flexEnd"
        border={{ top: { style: 'solid', width: 's1', color: 'grey400' } }}
      >
        <F0Button
          size="md"
          icon={Check}
          onClick={save}
          label={i18n.t('common.save')}
          disabled={!jobStructure || creatingCostCentersConfiguration}
        />
      </Box>
    </Box>
  )
}

export { CostCenterConfiguration }
